import { useId, useState } from 'react';
import { notification, Popconfirm, Switch } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { getApiErrorCode, getApiErrorMessage, formatNumber } from 'shared/utils';
import { colorPalette } from 'shared/constants';
import { useDateFormatter } from 'shared/hooks/dates.hooks';
import {
  useQualityMetricConfiguration,
  useUpdateQualityMetric,
} from 'shared/hooks/quality-metric.hooks';
import {
  PULL_KEY_MAX,
  PULL_MAPPERS,
  QUALITY_METRIC_ERROR_CODES,
} from 'shared/constants/quality-metric.constants';
import { Button, Select, TextInput } from 'src/components/design-system';
import { useAccessiblePopconfirm } from 'src/components/design-system/popconfirm/hooks/use-accessible-popconfirm';
import type {
  PullConfiguration,
  PullMapper,
  QualityMetric,
} from 'shared/api/quality-metric-rq.service.api';
import { useQualityMetricsManagementContext } from '../quality-metrics-management.context';
import {
  hasPullChange,
  toInternalConfigurations,
  type PullDraft,
} from '../quality-metrics-management.utils';
import { Stat } from './management-stat';
import styles from '../quality-metrics-management.module.scss';

// Parser names are system names, not prose: shown as the external systems
// write them rather than translated.
const mapperLabel = (mapper: PullMapper) => mapper.toUpperCase();

const MAPPER_OPTIONS = PULL_MAPPERS.map((mapper: PullMapper) => ({
  value: mapper,
  label: mapperLabel(mapper),
}));

const toDraft = (pull: PullConfiguration | null | undefined): PullDraft => ({
  key: pull?.key ?? '',
  enabled: !!pull?.enabled,
  mapper: pull?.mapper ?? null,
});

/**
 * Fills `{name}` placeholders in a translated string. A namespace lookup can
 * miss, so this also keeps a missing key from turning into a crash on
 * `.replace` — the line renders empty instead.
 */
const interpolate = (template: string | undefined, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [name, value]) => text.replace(`{${name}}`, value),
    template ?? '',
  );

interface ManagementDataSourceSectionProps {
  metric: QualityMetric;
}

/**
 * Which external measure feeds this metric, whether the sync service pulls it,
 * and which parser reads the export. Saves on click like the resources tab
 * rather than through the metric form: this is operational wiring, not part of
 * the definition an admin edits in one go.
 *
 * @param props.metric - The selected metric. Passed in rather than read from
 *   the context's nullable `selectedMetric`, because the section only renders
 *   under a selection. The measure key is capped at `PULL_KEY_MAX` (100 chars,
 *   trimmed), which is what the backend accepts.
 * @returns The data-source stat grid, or its inline editor while editing.
 * @example
 * // Keyed on the id so switching metrics remounts it and the draft re-seeds
 * // without an effect.
 * <ManagementDataSourceSection key={metric.id} metric={metric} />
 */
export function ManagementDataSourceSection({ metric }: ManagementDataSourceSectionProps) {
  const { lang } = useQualityMetricsManagementContext();
  const { formatLongDateTime, formatRelativeTime } = useDateFormatter();
  // The organization's configuration is its own document, so the default
  // parser is read rather than taken off the organization in the store.
  const { data: configuration, isSuccess: isConfigurationLoaded } = useQualityMetricConfiguration();
  const organizationMapper = configuration?.mapper?.default ?? null;
  const [api, contextHolder] = notification.useNotification();
  const updateMetric = useUpdateQualityMetric();
  const switchId = useId();

  const pull = metric.internalConfigurations?.pull;
  const { lastIngestionAt } = metric;

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(() => toDraft(pull));
  const [keyError, setKeyError] = useState<string | undefined>(undefined);

  const draftKey = draft.key.trim();
  const changed = hasPullChange({ draft, metric });

  // Three states, not two: a read still in flight is not a confirmed absence,
  // so it stays on the plain label rather than claiming no default is set.
  const fallbackLabel = organizationMapper
    ? interpolate(lang.PULL_MAPPER_DEFAULT_RESOLVED, { mapper: mapperLabel(organizationMapper) })
    : isConfigurationLoaded
      ? (lang.PULL_MAPPER_DEFAULT_MISSING ?? '')
      : (lang.PULL_MAPPER_DEFAULT ?? '');

  const close = () => {
    setIsEditing(false);
    setKeyError(undefined);
  };

  const save = async () => {
    if (draftKey.length > PULL_KEY_MAX) {
      setKeyError(
        interpolate(lang.VALIDATION_PULL_KEY_LENGTH, { max: formatNumber(PULL_KEY_MAX) }),
      );
      return;
    }

    const internalConfigurations = toInternalConfigurations({ draft, metric });
    // Nothing to say to the server: either nothing moved, or an unmapped metric
    // was left unmapped. Sending a no-op would still bump the metric.
    if (!changed || !internalConfigurations) {
      close();
      return;
    }

    try {
      await updateMetric.mutateAsync({
        id: metric.id,
        payload: { internalConfigurations },
      });
      api.success({ message: lang.METRIC_UPDATED, placement: 'bottomRight' });
      close();
    } catch (error) {
      // A taken key is about this one field, so it is marked as well as
      // toasted; the server message names the metric already holding it.
      if (getApiErrorCode(error) === QUALITY_METRIC_ERROR_CODES.PULL_KEY_TAKEN) {
        setKeyError(lang.ERROR_PULL_KEY_TAKEN);
      }
      api.error({
        message: getApiErrorMessage(error) || lang.ERROR_GENERIC,
        placement: 'bottomRight',
      });
    }
  };

  const popconfirmProps = useAccessiblePopconfirm({ onConfirm: save });

  // One line per thing that actually moves, kept as separate elements so no
  // sentence is assembled from fragments.
  const confirmLines: string[] = [];
  const previousKey = pull?.key ?? '';
  if (draftKey !== previousKey) {
    if (!draftKey) confirmLines.push(lang.CONFIRM_PULL_KEY_CLEARED ?? '');
    else if (!previousKey) {
      confirmLines.push(interpolate(lang.CONFIRM_PULL_KEY_SET, { to: draftKey }));
    } else {
      confirmLines.push(
        interpolate(lang.CONFIRM_PULL_KEY_CHANGE, { from: previousKey, to: draftKey }),
      );
    }
  }
  // Both suppressed when the key is being cleared — "no longer pulled" says it.
  if (draftKey && !!draft.enabled !== !!pull?.enabled) {
    confirmLines.push(
      (draft.enabled ? lang.CONFIRM_PULL_SYNC_ON : lang.CONFIRM_PULL_SYNC_OFF) ?? '',
    );
  }
  if (draftKey && (draft.mapper || null) !== (pull?.mapper ?? null)) {
    confirmLines.push(
      interpolate(lang.CONFIRM_PULL_MAPPER_CHANGE, {
        from: pull?.mapper ? mapperLabel(pull.mapper) : fallbackLabel,
        to: draft.mapper ? mapperLabel(draft.mapper) : fallbackLabel,
      }),
    );
  }

  // Enabled with no parser anywhere is silently skipped by the sync service.
  // Gated on the read having landed, or it accuses a correct organization while
  // the read is in flight, and permanently if it fails.
  const missingMapper =
    isConfigurationLoaded && pull?.enabled && !pull.mapper && !organizationMapper;

  const saveButton = (
    <Button type="primary" loading={updateMetric.isPending} onClick={changed ? undefined : save}>
      {lang.SAVE}
    </Button>
  );

  return (
    <>
      {contextHolder}
      <div className={styles.dataSourceHeader}>
        <h3 className={styles.sectionTitle}>{lang.SECTION_DATA_SOURCE}</h3>
        {!isEditing && (
          <Button
            type="default"
            onClick={() => {
              setDraft(toDraft(pull));
              setKeyError(undefined);
              setIsEditing(true);
            }}>
            <FontAwesomeIcon icon={faPencil} /> {lang.EDIT}
          </Button>
        )}
      </div>

      {isEditing ? (
        <>
          <p className={styles.fieldWarning} style={{ color: colorPalette.orange }}>
            <FontAwesomeIcon icon={faTriangleExclamation} /> {lang.WARN_PULL_CONFIG}
          </p>

          <TextInput
            label={lang.FIELD_PULL_KEY}
            name="pullKey"
            value={draft.key}
            onChange={e => {
              setKeyError(undefined);
              setDraft(current => ({ ...current, key: e.target.value }));
            }}
            error={keyError}
            maxLength={PULL_KEY_MAX}
            size="middle"
          />

          <Select
            label={lang.FIELD_PULL_MAPPER}
            name="mapper"
            value={draft.mapper ?? undefined}
            // Cleared means "fall back to the organization", which the server
            // stores as `null` — so the placeholder is the resolved default.
            placeholder={fallbackLabel}
            options={MAPPER_OPTIONS}
            disabled={!draftKey}
            onChange={(name, value) => setDraft(current => ({ ...current, mapper: value ?? null }))}
          />
          <p className={styles.fieldHint}>{lang.HINT_PULL_MAPPER}</p>

          <div className={styles.switchWrapper}>
            <Switch
              id={switchId}
              style={{ width: '2rem' }}
              checked={draft.enabled}
              // Sync has nothing to pull without a measure key.
              disabled={!draftKey}
              onChange={enabled => setDraft(current => ({ ...current, enabled }))}
            />
            <label htmlFor={switchId}>{lang.FIELD_PULL_ENABLED}</label>
          </div>

          <div className={styles.dataSourceActions}>
            <Button type="default" onClick={close}>
              {lang.CANCEL}
            </Button>
            {changed ? (
              <Popconfirm
                {...popconfirmProps}
                title={lang.CONFIRM_PULL_TITLE}
                description={confirmLines.map(line => (
                  <p key={line} className={styles.confirmLine}>
                    {line}
                  </p>
                ))}
                okText={lang.SAVE}
                cancelText={lang.CANCEL}
                okButtonProps={{ type: 'primary' }}
                cancelButtonProps={{ type: 'default' }}>
                {saveButton}
              </Popconfirm>
            ) : (
              saveButton
            )}
          </div>
        </>
      ) : (
        <>
          <div className={styles.statGrid}>
            <Stat label={lang.FIELD_PULL_KEY} value={pull?.key || lang.NOT_SET} />
            <Stat
              label={lang.FIELD_PULL_ENABLED}
              value={pull ? (pull.enabled ? lang.YES : lang.NO) : lang.NOT_SET}
            />
            <Stat
              label={lang.FIELD_PULL_MAPPER}
              value={pull ? (pull.mapper ? mapperLabel(pull.mapper) : fallbackLabel) : lang.NOT_SET}
            />
            {/* Shown for an unmapped metric too: records also arrive by CSV
                import, and "when did this last get data" is the same question. */}
            <Stat
              label={lang.STAT_LAST_INGESTION}
              value={lastIngestionAt ? formatLongDateTime(lastIngestionAt) : lang.NEVER}
              hint={lastIngestionAt ? formatRelativeTime(lastIngestionAt) : undefined}
            />
          </div>

          {missingMapper && (
            <p className={styles.fieldWarning} style={{ color: colorPalette.orange }}>
              <FontAwesomeIcon icon={faTriangleExclamation} /> {lang.WARN_PULL_NO_MAPPER}
            </p>
          )}
          {pull?.enabled && !lastIngestionAt && (
            <p className={styles.fieldWarning} style={{ color: colorPalette.orange }}>
              <FontAwesomeIcon icon={faTriangleExclamation} /> {lang.WARN_PULL_NO_DATA}
            </p>
          )}
          {pull?.key && !pull.enabled && (
            <p className={styles.fieldHint}>{lang.HINT_PULL_NOT_SYNCING}</p>
          )}
        </>
      )}
    </>
  );
}
